// Consent gate (widget side). Ad tags run ONLY after an explicit Accept, never under GPC.
export type Choice = "accepted" | "declined";
export interface Tag { name: string; src: string }

/** GPC on = declined, whatever was stored. No stored choice and no GPC = undecided (nothing loads). */
export function effectiveConsent(stored: Choice | undefined | null, gpc: boolean): Choice | undefined {
  if (gpc) return "declined";
  return stored === "accepted" || stored === "declined" ? stored : undefined;
}

export function gpcOn(nav: { globalPrivacyControl?: boolean } | undefined): boolean {
  return nav?.globalPrivacyControl === true;
}

/** The tag gate. Returns the names of the tags it ran. */
export function runTags(tags: Tag[], consent: Choice | undefined, load: (t: Tag) => void): string[] {
  if (consent !== "accepted") return [];
  const ran: string[] = [];
  for (const t of tags) { if (t && typeof t.src === "string" && t.src) { load(t); ran.push(t.name); } }
  return ran;
}

/** A relative tag src belongs to the engine, not the host page: resolve it against the engine origin (apiBase). Absolute srcs pass through. */
export function resolveTagSrc(src: string, apiBase: string): string {
  if (!apiBase || /^([a-z][a-z0-9+.-]*:)?\/\//i.test(src)) return src;
  try { return new URL(src, apiBase).href; } catch { return src; }
}

export function loadScript(t: Tag, apiBase = ""): void {
  const s = document.createElement("script");
  s.src = resolveTagSrc(t.src, apiBase); s.async = true; s.dataset.tag = t.name;
  document.head.appendChild(s);
}

const KEY = "sorted:consent";
interface BarOpts { slug: string; apiBase: string; tags: Tag[]; visitId: () => string; ensureVisit: () => Promise<void> }

/** Small bar inside the widget's Shadow DOM. The quiz never waits on it. */
export function mountConsent(root: ShadowRoot, o: BarOpts): void {
  const gpc = gpcOn(navigator as { globalPrivacyControl?: boolean });
  let stored: Choice | undefined;
  try { stored = (localStorage.getItem(KEY) as Choice | null) ?? undefined; } catch { /* no storage */ }
  const bar = document.createElement("div");
  bar.className = o.apiBase ? "consent consent--embed" : "consent"; // embeds pass an absolute apiBase; the hosted /q page uses ""
  bar.setAttribute("role", "region");
  bar.setAttribute("aria-label", "Cookie choice");
  root.appendChild(bar);

  const record = (choice: Choice) => {
    void (async () => {
      try {
        await o.ensureVisit();
        await fetch(`${o.apiBase}/api/visit/${o.visitId()}/consent`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ choice, gpc }) });
      } catch { /* the quiz does not depend on this */ }
    })();
  };
  const settle = (choice: Choice | undefined) => {
    runTags(o.tags, choice, (t) => loadScript(t, o.apiBase));
    bar.remove();
  };

  if (gpc) {
    bar.innerHTML = `<p>Your browser asked us not to share your data — we won't. <a href="${o.apiBase}/privacy" target="_blank" rel="noopener">Privacy</a></p><div class="consent__btns"><button class="consent__btn" data-ok>OK</button></div>`;
    bar.querySelector("[data-ok]")!.addEventListener("click", () => bar.remove());
    if (stored !== "declined") { try { localStorage.setItem(KEY, "declined"); } catch { /* ignore */ } record("declined"); }
    return;
  }
  const c = effectiveConsent(stored, false);
  if (c) { settle(c); return; }
  bar.innerHTML = `<p>We use cookies to measure which ads bring people here. <a href="${o.apiBase}/privacy" target="_blank" rel="noopener">Privacy</a></p><div class="consent__btns"><button class="consent__btn" data-no>No thanks</button><button class="consent__btn consent__btn--yes" data-yes>Accept</button></div>`;
  const pick = (choice: Choice) => { try { localStorage.setItem(KEY, choice); } catch { /* ignore */ } record(choice); settle(choice); };
  bar.querySelector("[data-yes]")!.addEventListener("click", () => pick("accepted"));
  bar.querySelector("[data-no]")!.addEventListener("click", () => pick("declined"));
}
